import { MbscCalendarEvent } from '@mobiscroll/react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { Grid } from '@mui/material';

import event from 'assets/icons/Event.svg';
import { fetchAllEvents } from 'core/API/events';
import { LocalEvents } from 'shared/components/LocalEvents';
import Sidebar from 'core/components/Sidebar/Sidebar';
import Modal from 'core/components/Modal/Modal';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useIsMount } from 'core/hooks/useIsMounted';
import { useMobileDetect } from 'core/hooks/useMobileDetect';
import useModal from 'core/hooks/useModal';
import useSearchQuery from 'core/hooks/useQuery';
import { useAuth } from 'module/auth/context/AuthContext';
import AddEventModal from 'module/schedule/Modals/AddEventModal/AddEventModal';
import CustomerDatabaseModal from 'module/schedule/Modals/CustomerDatabase/CustomerDatabaseModal';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import { IWorkplaces } from 'module/workplaces/interface/WorkplacesInterface';
import getTimeWithTimezone from 'shared/utils/dates/formatTimeWithTimezone';
import CustomerTable from '../../components/featured/customer-table/CustomerTable';
import { useCalendarView } from 'core/hooks/useCalendarView';

import DesktopPage from './pages/DesktopPage/DesktopPage';
import MobilePage from './pages/MobilePage/MobilePage';
import { EventsHeader } from './components/EventsHeader';
import { getScheduleDateRange } from '../schedule/pages/SchedulePage.utils';
import './Events.style.scss';

type DateInput = string | Date | null | undefined;

const normalizeDateInput = (value: DateInput): Date | null => {
	if (value === null || value === undefined) {
		return null;
	}

	const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

const parseTimeParts = (time?: string | null) => {
	if (!time) return null;
	const [hoursStr, minutesStr, secondsStr] = time.split(':');
	const hours = Number(hoursStr);
	const minutes = Number(minutesStr ?? '0');
	const seconds = Number(secondsStr ?? '0');

	if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) {
		return null;
	}

	return { hours, minutes, seconds };
};

const combineDateAndTime = (dateValue: DateInput, timeValue?: string | null): Date | null => {
	const date = normalizeDateInput(dateValue);
	if (!date) return null;

	const timeParts = parseTimeParts(timeValue);
	if (timeParts) {
		const { hours, minutes, seconds } = timeParts;
		date.setHours(hours, minutes, seconds ?? 0, 0);
	}

	return date;
};

export default function Events(): JSX.Element {
	const auth = useAuth();
	const isMounted = useIsMount();
	const queryClient = useQueryClient();
	const { push } = useHistory();
	const { workplaceId, timezone } = useWorkplacesContext();
	const { showToast } = useToastContext();
	const isMobile = useMobileDetect().isMobile();

	// Permission checks for Events (module-level, like Logbook)
	const {
		hasModuleViewPermission,
		hasModuleEditPermission,
		hasModuleDeletePermission,
		hasModuleExecutePermission,
	} = usePermissionCheck();

	// Events permission checks
	const canViewEvents = hasModuleViewPermission('Events');
	const canEditEvents = hasModuleEditPermission('Events');
	const canDeleteEvents = hasModuleDeletePermission('Events');
	const canExecuteEvents = hasModuleExecutePermission('Events');
	const canCreateEvents = canEditEvents || canExecuteEvents; // Can create if has edit or execute permission

	const location = useSearchQuery();

	const isValidParam = (paramName: string) => {
		const paramValue = location.get(paramName);
		let isValid = true;

		if (paramValue === 'undefined') {
			isValid = false;
		}
		if (paramValue === 'null') {
			isValid = false;
		}
		if (!paramValue) {
			isValid = false;
		}
		return isValid ? location.get(paramName) : paramName === 'time' ? 'day' : 'event';
	};

	const [timeView, _setView] = useState<'day' | 'week'>(isValidParam('time') as 'day' | 'week');
	const { calendarView } = useCalendarView();

	// Initialize selectedDate from localStorage or today
	const [selectedDate, setSelectedDate] = useState(() => {
		const storedDate = localStorage.getItem('events-selected-date');
		return storedDate || moment().format('YYYY-MM-DD');
	});

	// Initialize fromDate based on selectedDate
	const [fromDate, setFromDate] = useState(() => {
		const storedDate = localStorage.getItem('events-selected-date');
		const dateToUse = storedDate || moment().format('YYYY-MM-DD');
		const dateMoment = moment(dateToUse);
		return dateMoment.format('YYYY-MM-DD HH:mm:ss');
	});

	// Sync selectedDate with localStorage and update fromDate when selectedDate changes
	useEffect(() => {
		localStorage.setItem('events-selected-date', selectedDate);
		// Update fromDate based on selectedDate
		const dateMoment = moment(selectedDate);
		const formattedDate = dateMoment.format('YYYY-MM-DD HH:mm:ss');
		setFromDate(formattedDate);
	}, [selectedDate]);

	// Listen to date changes from header
	useEffect(() => {
		const handleDateChange = (event: CustomEvent) => {
			const newDate = event.detail;
			setSelectedDate(newDate);
		};

		window.addEventListener('events-date-changed', handleDateChange as EventListener);
		return () => {
			window.removeEventListener('events-date-changed', handleDateChange as EventListener);
		};
	}, []);

	const [toDate, setToDate] = useState(() => {
		const [, formatedLastDay] = getScheduleDateRange(timezone, timeView, isMobile);
		return formatedLastDay;
	});

	const [allEvents, setAllEvents] = useState<MbscCalendarEvent[]>([]);
	const [editEvent, setEditEvent] = useState(null);
	const [addNewEvent, setAddNewEvent] = useState<any>();
	const workplacePostcode = localStorage.getItem('workplace-postcode');

	const {
		open: isAddEventModalOpen,
		openModal: openAddEventModal,
		closeModal: closeAddEventModal,
	} = useModal();

	const {
		open: isEditEventModalOpen,
		openModal: openEditEventModal,
		closeModal: closeEditEventModal,
	} = useModal();

	const {
		open: isCustomerDatabaseModalOpen,
		openModal: openCustomerDatabaseModal,
		closeModal: closeCustomerDatabaseModal,
	} = useModal();

	const workplacesData = queryClient.getQueryData<IWorkplaces>(['workplaces']);

	// Extract date part from fromDate for startDate parameter
	const startDateOnly = fromDate ? moment(fromDate).format('YYYY-MM-DD') : null;

	const { refetch: refetchEvents } = useQuery(
		['events', workplaceId, startDateOnly],
		() =>
			fetchAllEvents({
				workplaceId,
				startDate: startDateOnly || undefined,
				pagination: false,
			}),
		{
			refetchInterval: 600000,
			enabled: !!startDateOnly,
			onSuccess: async (data) => {
				const events = data?.member || [];
				
				const mappedEvents: any[] = [];
				
				events.forEach((event: any) => {
					const startDateTime =
						combineDateAndTime(event.startDate, event.startTime) ||
						combineDateAndTime(event.start, event.startTime) ||
						new Date();
					
					const endDateTime =
						combineDateAndTime(event.endDate, event.endTime) ||
						combineDateAndTime(event.end, event.endTime) ||
						new Date();

					const eventTypeId = event.type 
						? parseInt(event.type.split('/').pop()) 
						: null;
					
					console.log(`Event ${event.id} - eventTypeId: ${eventTypeId}, type IRI: ${event.type}`);

					const locationId = event.location 
						? parseInt(event.location.split('/').pop()) 
						: null;

					const customerId = event.customer 
						? parseInt(event.customer.split('/').pop()) 
						: null;

					const sharedEventFields = {
						start: startDateTime,
						end: endDateTime,
						title: event.name,
						name: event.name,
						location: locationId,
						eventType: eventTypeId,
						customer: customerId,
						taxExemptId: event.taxExemptId || '',
						notes: event.notes || '',
						numberOfAttendees: event.numberOfAttendees || 0,
						background_color: 'rgb(68, 99, 136)',
						text_color: 'white',
						originalEvent: event,
					};

					if (event.eventStaffing && event.eventStaffing.length > 0) {
						event.eventStaffing.forEach((staffing: any, index: number) => {
							mappedEvents.push({
								id: `${event.id}-${staffing.jobCode || 'job'}-${index}`,
								resource: eventTypeId || 'events-row',
								eventStaffing: [staffing],
								numberOfAdditionalStaff: staffing.numberOfAdditionalStaff || 0,
								...sharedEventFields,
							} as MbscCalendarEvent);
						});
					} else {
						mappedEvents.push({
							id: event.id,
							resource: eventTypeId || 'events-row',
							eventStaffing: [],
							numberOfAdditionalStaff: 0,
							...sharedEventFields,
						} as MbscCalendarEvent);
					}
				});

				const eventsOverlap = (event1: any, event2: any): boolean => {
					const start1 = new Date(event1.start).getTime();
					const end1 = new Date(event1.end).getTime();
					const start2 = new Date(event2.start).getTime();
					const end2 = new Date(event2.end).getTime();
					
					return (start1 < end2 && start2 < end1);
				};

				const eventsByResource: Map<string | number, any[]> = new Map();
				mappedEvents.forEach((event: any) => {
					const resourceKey = event.resource || 'events-row';
					if (!eventsByResource.has(resourceKey)) {
						eventsByResource.set(resourceKey, []);
					}
					eventsByResource.get(resourceKey)!.push(event);
				});

				const processedEvents: any[] = [];
				
				eventsByResource.forEach((eventsInResource: any[], baseResource: string | number) => {
					const sortedEvents = [...eventsInResource].sort((a, b) => {
						const startDiff = new Date(a.start).getTime() - new Date(b.start).getTime();
						if (startDiff !== 0) return startDiff;
						
						const endDiff = new Date(a.end).getTime() - new Date(b.end).getTime();
						if (endDiff !== 0) return endDiff;
						
						const idA = String(a.id || '');
						const idB = String(b.id || '');
						return idA.localeCompare(idB);
					});
					
					const rows: any[][] = [];
					
					sortedEvents.forEach((event: any) => {
						const availableRows: number[] = [];
						
						for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
							const eventsInRow = rows[rowIndex];
							const hasOverlap = eventsInRow.some((existingEvent: any) => 
								eventsOverlap(event, existingEvent)
							);
							
							if (!hasOverlap) {
								availableRows.push(rowIndex);
							}
						}
						
						let assignedRow: number;
						if (availableRows.length > 0) {
							let minEvents = Infinity;
							let bestRow = availableRows[0];
							
							availableRows.forEach((rowIndex) => {
								const eventCount = rows[rowIndex].length;
								if (eventCount < minEvents) {
									minEvents = eventCount;
									bestRow = rowIndex;
								}
							});
							
							assignedRow = bestRow;
						} else {
							assignedRow = rows.length;
							rows[assignedRow] = [];
						}
						
						rows[assignedRow].push(event);
						
						const rowResourceId = `${String(baseResource)}-row-${assignedRow}`;
						
						processedEvents.push({
							...event,
							resource: rowResourceId,
							originalResource: baseResource,
							rowNumber: assignedRow,
						});
					});
				});
				
				processedEvents.sort((a, b) => {
					const rowDiff = (a.rowNumber || 0) - (b.rowNumber || 0);
					if (rowDiff !== 0) return rowDiff;
					
					const startDiff = new Date(a.start).getTime() - new Date(b.start).getTime();
					return startDiff;
				});

				const dynamicRowResources = new Set<string | number>();
				processedEvents.forEach((event: any) => {
					dynamicRowResources.add(event.resource);
				});

				console.log('Mapped Events:', processedEvents);
				console.log('Total Events Mapped:', processedEvents.length);
				console.log('Dynamic Row Resources:', Array.from(dynamicRowResources));
				if (processedEvents.length > 0) {
					console.log('First Event Sample:', {
						id: processedEvents[0].id,
						resource: processedEvents[0].resource,
						start: processedEvents[0].start,
						end: processedEvents[0].end,
						title: processedEvents[0].title,
					});
				}
			setAllEvents(processedEvents);
		},
	}
);

	useEffect(() => {
		if (!isMounted) {
			refetchEvents();
		}
	}, [fromDate, toDate, isMounted, refetchEvents]);

	const updateTimeRange = (firstday: Date, lastday: Date) => {
		const [formatedFirstDay, formatedLastDay] = getTimeWithTimezone(firstday, lastday, timezone);
		setFromDate(formatedFirstDay);
		setToDate(formatedLastDay);
	};

	// Update date range when selectedDate changes (for day view) or when switching to week view
	useEffect(() => {
		if (calendarView === 'day') {
			// For day view, show only the selected day
			const selectedMoment = moment(selectedDate);
			const [formatedFirstDay, formatedLastDay] = getTimeWithTimezone(
				selectedMoment.startOf('day').toDate(),
				selectedMoment.endOf('day').toDate(),
				timezone
			);
			setFromDate(formatedFirstDay);
			setToDate(formatedLastDay);
		} else if (calendarView === 'week') {
			// For week view, show the week containing the selected date
			const selectedMoment = moment(selectedDate);
			const weekStart = selectedMoment.clone().startOf('week');
			const weekEnd = selectedMoment.clone().endOf('week');
			const [formatedFirstDay, formatedLastDay] = getTimeWithTimezone(
				weekStart.toDate(),
				weekEnd.toDate(),
				timezone
			);
			setFromDate(formatedFirstDay);
			setToDate(formatedLastDay);
		}
	}, [selectedDate, timezone, calendarView]);

	const openEditModal = (event: any) => {
		// Check if user has permission to edit events
		if (!canEditEvents) {
			showToast('error', 'You do not have permission to edit events');
			return;
		}

		const actualEventId = event?.originalEvent?.id || event?.id;
		const eventData = event?.originalEvent || event;
		
		// Extract event date and update selectedDate to keep calendar on that date
		const eventStartDate = eventData?.startDate || eventData?.start || event?.start;
		if (eventStartDate) {
			const eventDate = moment(eventStartDate).format('YYYY-MM-DD');
			// Update selectedDate to the event's date so calendar stays on that date
			setSelectedDate(eventDate);
			// Also update localStorage to persist the date
			localStorage.setItem('events-selected-date', eventDate);
		}
		
		// Pass the original startDate and endDate from API response directly
		// No conversion needed - CreateEvent will extract the date part
		const eventDataWithTime = {
			...eventData,
			// Use original startDate/endDate from API if available
			startDate: eventData?.startDate || eventData?.start || null,
			endDate: eventData?.endDate || eventData?.end || null,
			// Use original startTime/endTime from API if available
			startTime: eventData?.startTime || null,
			endTime: eventData?.endTime || null,
		};
		
		push({
			pathname: '/manager/events/create',
			state: {
				editEvent: eventDataWithTime,
				eventId: actualEventId,
			},
		});
	};

	const openAddModal = (event: any) => {
		// Check if user has permission to create events
		if (!canCreateEvents) {
			showToast('error', 'You do not have permission to create events');
			return;
		}

		const state: any = {};
		
		const roundToNearestQuarter = (date: Date): string => {
			const minutes = date.getMinutes();
			const roundedMinutes = Math.round(minutes / 15) * 15;
			date.setMinutes(roundedMinutes);
			date.setSeconds(0);
			return moment(date).format('HH:mm');
		};
		
		if (event?.action === 'drag' || event?.action === 'externalDrop') {
			if (event.event?.start) {
				const startDate = new Date(event.event.start);
				state.date = startDate;
				state.startTime = roundToNearestQuarter(new Date(startDate));
			}
			if (event.event?.end) {
				const endDate = new Date(event.event.end);
				state.endTime = roundToNearestQuarter(new Date(endDate));
			}
			if (event.event?.resource) {
				state.resource = event.event.resource;
			}
		} else if (event?.date) {
			const clickedDate = new Date(event.date);
			state.date = clickedDate;
			
			state.startTime = roundToNearestQuarter(new Date(clickedDate));
			
			const endDate = new Date(clickedDate);
			endDate.setHours(endDate.getHours() + 1);
			state.endTime = roundToNearestQuarter(endDate);
			
			if (event?.resource) {
				state.resource = event.resource;
			}
		}
		
		push({
			pathname: '/manager/events/create',
			state: state,
		});
	};

	const closeEditModalHandler = () => {
		refetchEvents();
	};

	const closeAddModalHandler = () => {
		refetchEvents();
		closeAddEventModal();
	};

	const setCalendarTypeHandler = (view: 'schedule' | 'request' | 'event') => {
		if (view === 'schedule') {
			push('/manager/hyperscheduler?view=schedule&time=day');
		} else if (view === 'request') {
			push('/manager/hyperscheduler?view=request&time=day');
		}
	};

	// If user doesn't have view permission, show access denied message
	if (!canViewEvents) {
		return (
			<div style={{ 
				display: 'flex', 
				justifyContent: 'center', 
				alignItems: 'center', 
				minHeight: 'calc(100vh - 200px)',
				padding: '20px'
			}}>
				<div style={{
					backgroundColor: '#fff',
					borderRadius: '10px',
					boxShadow: '0 2px 5px rgba(0, 0, 0, 0.06)',
					padding: '40px 60px',
					textAlign: 'center',
					maxWidth: '500px',
					width: '100%'
				}}>
					<h2 style={{ 
						margin: '0 0 16px 0', 
						fontSize: '24px', 
						fontWeight: 600,
						color: '#333'
					}}>
						Access Denied
					</h2>
					<p style={{ 
						margin: 0, 
						fontSize: '16px', 
						color: '#666',
						lineHeight: '1.5'
					}}>
						You do not have permission to view events.
					</p>
				</div>
			</div>
		);
	}

	return (
		<>
			<section className="Content EventsContent">
				<div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
					{!isMobile && (
						<div style={{ marginBottom: '20px', width: '100%' }}>
							<Grid container spacing={2}>
								<Grid item xs={6} style={{ height: '300px' }}>
									<div className="card alerts" style={{ height: '100%' }}>
										<div className="events-header">
											<h2>Mabel A.I. - Local Events</h2>
										</div>
										<LocalEvents workplaceId={workplaceId} />
									</div>
								</Grid>
								<Grid item xs={6} style={{ height: '300px' }}>
									<CustomerTable title="Customer" />
								</Grid>
							</Grid>
						</div>
					)}
					<div className="draggable" style={{ width: '100%', flex: 1 }}>
					{isMobile ? (
						<MobilePage
							openEditShiftModal={openEditModal}
							allSchedules={[]}
							allEvents={allEvents}
							updateTimeRange={updateTimeRange}
							setCalendarType={setCalendarTypeHandler}
							calendarType="event"
							openRequestsModal={() => {}}
						/>
					) : (
						<DesktopPage
							openEditShiftModal={openEditModal}
							openAddShiftModal={openAddModal}
							allSchedules={[]}
							allEvents={allEvents}
							workplacesData={workplacesData!}
							updateTimeRange={updateTimeRange}
							setCalendarType={setCalendarTypeHandler}
							calendarType="event"
							openRequestsModal={() => {}}
							openAddEventModal={openAddModal}
							openEditEventModal={openEditModal}
							reassignShiftToAnotherUser={() => {}}
							refetch={refetchEvents}
							updateOneShiftHandler={() => {}}
							dragTimeStep={15}
							renderHeaderOverride={() => {
								if (!workplacesData) return null;
								return (
									<EventsHeader
										workplacesData={workplacesData}
										workplaceId={workplaceId}
										canCreateEvents={canCreateEvents}
									/>
								);
							}}
							selectedDate={selectedDate}
							canEditEvents={canEditEvents}
							canDeleteEvents={canDeleteEvents}
						/>
					)}
					</div>
				</div>
			</section>

			{isAddEventModalOpen ? (
				<Sidebar
					icon={event}
					title="Create a new event"
					close={closeAddModalHandler}
					render={() => (
						<AddEventModal closeEventModal={closeAddModalHandler} addNewEvent={addNewEvent} />
					)}
				/>
			) : null}

			{isCustomerDatabaseModalOpen ? (
				<Modal
					icon={event}
					title="Customer database"
					close={() => {
						closeCustomerDatabaseModal();
					}}
					bigModal
					render={() => (
						<CustomerDatabaseModal
							closeModal={() => {
								closeCustomerDatabaseModal();
							}}
						/>
					)}
				/>
			) : null}
		</>
	);
}
